import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Instagram, Mail, Send, Loader2 } from "lucide-react";
import { EMAIL, GROUP_OPTIONS, INSTAGRAM_URL, useLang } from "@/i18n";

const EMPTY = { student_name: "", guardian_name: "", phone: "", email: "", selected_group: "", comments: "" };

export default function Contact() {
  const { t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/inquiries`, form);
      toast.success(c.success, { style: { background: "#14141A", border: "1px solid #FF007A" } });
      setForm(EMPTY);
    } catch (err) {
      toast.error(err?.response?.data?.detail || c.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-zinc-500 outline-none transition-colors duration-300 focus:border-accent";

  return (
    <section id="contacto" className="px-5 py-24 sm:px-8 sm:py-32">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 lg:grid-cols-2">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            className="mb-4 font-mono text-xs font-bold uppercase tracking-[0.3em] text-accent"
          >
            {c.label}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="font-display text-3xl font-extrabold uppercase tracking-tight sm:text-5xl"
          >
            {c.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.7, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
            className="mt-2 font-display text-2xl font-black uppercase tracking-tight text-outline-pink sm:text-4xl"
          >
            {c.join}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-4 max-w-md text-sm text-zinc-400 sm:text-base"
          >
            {c.subtitle}
          </motion.p>

          <div className="mt-10 space-y-4">
            <motion.a
              data-testid="contact-instagram-link"
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="group flex items-center justify-between rounded-xl border border-white/10 bg-card p-5 transition-all duration-300 hover:border-accent/60"
            >
              <span className="flex items-center gap-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent/15 text-accent">
                  <Instagram size={20} />
                </span>
                <span>
                  <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                    {c.igLabel}
                  </span>
                  <span className="block font-bold text-white group-hover:text-accent">@oficialkhouse</span>
                </span>
              </span>
              <Send size={16} className="text-zinc-500 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-accent" />
            </motion.a>

            <motion.a
              data-testid="contact-email-link"
              href={`mailto:${EMAIL}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.5, delay: 0.25 }}
              className="group flex items-center justify-between rounded-xl border border-white/10 bg-card p-5 transition-all duration-300 hover:border-accent/60"
            >
              <span className="flex items-center gap-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent/15 text-accent">
                  <Mail size={20} />
                </span>
                <span>
                  <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                    {c.emailLabel}
                  </span>
                  <span className="block font-bold text-white group-hover:text-accent">{EMAIL}</span>
                </span>
              </span>
              <Send size={16} className="text-zinc-500 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-accent" />
            </motion.a>
          </div>
        </div>

        <motion.form
          data-testid="registration-inquiry-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="relative overflow-hidden rounded-2xl border border-white/10 bg-panel p-6 shadow-[0_0_40px_rgba(255,0,122,0.12)] sm:p-8"
        >
          <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-accent/15 blur-[80px]" />
          <div className="relative space-y-5">
            <div>
              <label htmlFor="student_name" className="mb-2 block font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">
                {c.student} *
              </label>
              <input id="student_name" data-testid="form-input-student-name" required value={form.student_name} onChange={set("student_name")} className={inputCls} />
            </div>
            <div>
              <label htmlFor="guardian_name" className="mb-2 block font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">
                {c.guardian}
              </label>
              <input id="guardian_name" data-testid="form-input-guardian-name" value={form.guardian_name} onChange={set("guardian_name")} className={inputCls} />
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="phone" className="mb-2 block font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">
                  {c.phone} *
                </label>
                <input id="phone" data-testid="form-input-phone" type="tel" required value={form.phone} onChange={set("phone")} className={inputCls} />
              </div>
              <div>
                <label htmlFor="email" className="mb-2 block font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">
                  {c.email} *
                </label>
                <input id="email" data-testid="form-input-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} />
              </div>
            </div>
            <div>
              <label htmlFor="selected_group" className="mb-2 block font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">
                {c.group} *
              </label>
              <select id="selected_group" data-testid="form-select-group" required value={form.selected_group} onChange={set("selected_group")} className={`${inputCls} appearance-none`}>
                <option value="" disabled className="bg-panel">{c.groupPlaceholder}</option>
                {GROUP_OPTIONS.map((o) => (
                  <option key={o} value={o} className="bg-panel">{o}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="comments" className="mb-2 block font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">
                {c.comments}
              </label>
              <textarea id="comments" data-testid="form-input-comments" rows={3} value={form.comments} onChange={set("comments")} placeholder={c.commentsPlaceholder} className={`${inputCls} resize-none`} />
            </div>
            <button
              type="submit"
              data-testid="submit-registration-btn"
              disabled={sending}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-accent px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.15em] text-white shadow-[0_0_28px_rgba(255,0,122,0.4)] transition-all duration-300 hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              {sending ? c.sending : c.submit}
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}
